import { useEffect, useRef, useState, useCallback } from 'react';
import {
  FluentProvider, makeStyles, MessageBar, MessageBarBody, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Button,
} from '@fluentui/react-components';
import { harleysTheme } from './services/theme';
import { HcpsHeader } from './components/HcpsHeader';
import { Breadcrumbs, Crumb } from './components/Breadcrumbs';
import { SafetyLoadingPanel, SplashScreen } from './components/SplashScreen';
import { JobsListScreen } from './components/JobsListScreen';
import { JobDetailScreen } from './components/JobDetailScreen';
import { StopScreen } from './components/StopScreen';
import { ThinkScreen } from './components/ThinkScreen';
import { ActScreen } from './components/ActScreen';
import { ReviewScreen } from './components/ReviewScreen';
import { SignOffScreen } from './components/SignOffScreen';
import { DoneScreen } from './components/DoneScreen';
import { AdminScreen } from './components/AdminScreen';
import { InfoModal } from './components/InfoModal';
import { getCurrentUser, isInShell } from './services/auth';
import { reportPendingCount, sendToShell } from '@harleys/shell-client';
import {
  getJobs, submitTake5, getM365Profile, getJobDetail, getConfig, ApiError,
} from './services/api';
import {
  AscoraJobSummary, AuthenticatedUser, Take5Submission, SubmissionResult,
  M365Profile, SupervisorCandidate, SiteContact, AppConfig,
} from './types';

type Screen =
  | { name: 'jobs' }
  | { name: 'jobDetail'; job: AscoraJobSummary }
  | { name: 'stop'; job: AscoraJobSummary }
  | { name: 'think'; job: AscoraJobSummary }
  | { name: 'act'; job: AscoraJobSummary }
  | { name: 'review'; job: AscoraJobSummary }
  | { name: 'sign'; job: AscoraJobSummary }
  | { name: 'done'; job: AscoraJobSummary; result: SubmissionResult }
  | { name: 'admin' };

const useStyles = makeStyles({
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '480px',
    marginLeft: 'auto',
    marginRight: 'auto',
    backgroundImage: 'linear-gradient(180deg, #202127 0 74px, #f7f8fa 74px 100%)',
    backgroundColor: '#f7f8fa',
  },
  spinnerOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: '#f7f8fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '24px',
    zIndex: 300,
  },
});

export function App() {
  const styles = useStyles();
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const [profile, setProfile] = useState<M365Profile | null>(null);
  const [supervisorCandidates, setSupervisorCandidates] = useState<SupervisorCandidate[]>([]);

  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<AscoraJobSummary[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState<string | null>(null);

  const [siteContact, setSiteContact] = useState<SiteContact | null>(null);
  const [contactLoading, setContactLoading] = useState(false);

  const [screen, updateScreen] = useState<Screen>({ name: 'jobs' });
  const [submission, setSubmission] = useState<Partial<Take5Submission>>({});
  const [infoKey, setInfoKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingScreen, setPendingScreen] = useState<Screen | null>(null);
  const submitFlight = useRef(false);
  const [visitedSteps, setVisitedSteps] = useState(new Set<string>());
  const workflowActive = ['stop', 'think', 'act', 'review', 'sign'].includes(screen.name);
  const setScreen = (next: Screen) => {
    if (submitFlight.current) return;
    if (workflowActive && ['jobs', 'jobDetail', 'admin'].includes(next.name)) setPendingScreen(next);
    else {
      if (['stop', 'think', 'act', 'review', 'sign'].includes(next.name)) setVisitedSteps(current => new Set([...current, next.name]));
      updateScreen(next);
    }
  };
  useEffect(() => {
    sendToShell({ type: 'app:leave-state', dirty: workflowActive, busy: Boolean(submitting) });
    const guard = (event: BeforeUnloadEvent) => {
      if (!workflowActive) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => {
      window.removeEventListener('beforeunload', guard);
      sendToShell({ type: 'app:leave-state', dirty: false, busy: false });
    };
  }, [workflowActive, submitting]);

  const adminRouteHandled = useRef(false);
  const adminRouteRequested = new URLSearchParams(window.location.search).get('admin') === '1';

  useEffect(() => {
    (async () => {
      try {
        const u = await getCurrentUser();
        setUser(u);
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : 'Authentication failed');
      }
    })();
  }, []);

  // Scroll to top on every screen change. Belt-and-braces because on iPhone
  // Teams the iframe lets the document scroll, on desktop the inner body div
  // scrolls — covering both.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* noop */ }
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    document.querySelectorAll('[data-screen-body]').forEach(el => {
      (el as HTMLElement).scrollTop = 0;
    });
  }, [screen.name]);

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    setJobsError(null);
    try {
      const { jobs } = await getJobs();
      setJobs(jobs);
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    try {
      const { profile, supervisorCandidates } = await getM365Profile();
      setProfile(profile);
      setSupervisorCandidates(supervisorCandidates);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('M365 profile load failed', err);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setConfigError(null);
    try {
      const res = await getConfig();
      setConfig(res.config);
      setIsAdmin(res.isAdmin);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load config');
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadJobs();
      loadProfile();
      loadConfig();
    }
  }, [user, loadJobs, loadProfile, loadConfig]);

  useEffect(() => {
    if (!adminRouteRequested || adminRouteHandled.current || !config) return;
    adminRouteHandled.current = true;
    setScreen({ name: 'admin' });
  }, [adminRouteRequested, config]);

  useEffect(() => {
    const pending = jobs.filter(j => j.takeFiveStatus !== 'Completed').length;
    reportPendingCount('take5', pending);
  }, [jobs]);

  const fetchSiteContact = useCallback(async (jobNumber: string) => {
    setContactLoading(true);
    setSiteContact(null);
    try {
      const detail = await getJobDetail(jobNumber);
      setSiteContact(detail.siteContact);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Site contact load failed', err);
      setSiteContact(null);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const onSelectJob = (job: AscoraJobSummary) => {
    setScreen({ name: 'jobDetail', job });
    fetchSiteContact(job.jobNumber);
  };

  const onStartT5 = (job: AscoraJobSummary) => {
    setVisitedSteps(new Set(['stop']));
    setSubmitError(null);
    setSubmission({ jobId: job.jobId, jobNumber: job.jobNumber });
    if (!siteContact && !contactLoading) {
      fetchSiteContact(job.jobNumber);
    }
    setScreen({ name: 'stop', job });
  };

  const handleSubmit = async (signOff: Take5Submission['signOff']) => {
    if (submitFlight.current) return;
    setSubmitError(null);
    if (!submission.stop || !submission.think || !submission.act || !submission.review) {
      setSubmitError('Complete every Take 5 step before submitting.');
      return;
    }
    const currentJob = 'job' in screen ? screen.job : null;
    if (!currentJob) return;

    const full: Take5Submission = {
      jobId: currentJob.jobId,
      jobNumber: currentJob.jobNumber,
      stop: submission.stop,
      think: submission.think,
      act: submission.act,
      review: submission.review,
      signOff,
    };

    submitFlight.current = true;
    setSubmitting('Saving your Take 5 and attaching it to the job…');
    try {
      const result = await submitTake5(full);
      setSubmitting(null);

      setJobs(js => js.map(j => j.jobId === currentJob.jobId
        ? { ...j, takeFiveStatus: 'Completed' as const, completedAt: result.submittedAt }
        : j));
      submitFlight.current = false;
      setScreen({ name: 'done', job: currentJob, result });
      setSubmission({});
    } catch (err) {
      setSubmitting(null);
      const msg = err instanceof ApiError
        ? `Submission failed (${err.status}): ${err.message}`
        : err instanceof Error ? err.message : 'Submission failed';
      setSubmitError(msg);
    } finally {
      submitFlight.current = false;
    }
  };

  const breadcrumbs: Crumb[] = (() => {
    const home: Crumb = { label: 'My Jobs', onClick: () => setScreen({ name: 'jobs' }) };
    if (screen.name === 'jobs') return [{ label: 'My Jobs' }];
    if (screen.name === 'admin') return [home, { label: 'Admin' }];
    const jobCrumb: Crumb = {
      label: screen.job.jobNumber,
      onClick: () => setScreen({ name: 'jobDetail', job: screen.job }),
    };
    switch (screen.name) {
      case 'jobDetail': return [home, { label: screen.job.jobNumber }];
      case 'stop':      return [home, jobCrumb, { label: 'STOP · Step 1 of 4' }];
      case 'think':     return [home, jobCrumb, { label: 'THINK · Step 2 of 4' }];
      case 'act':       return [home, jobCrumb, { label: 'ACT · Step 3 of 4' }];
      case 'review':    return [home, jobCrumb, { label: 'REVIEW · Step 4 of 4' }];
      case 'sign':      return [home, jobCrumb, { label: 'Sign-off' }];
      case 'done':      return [home, { label: screen.job.jobNumber }, { label: 'Complete' }];
    }
  })();

  const handleBreadcrumbBack = (() => {
    switch (screen.name) {
      case 'jobDetail':
      case 'admin':
        return () => setScreen({ name: 'jobs' });
      case 'stop':
        return () => setScreen({ name: 'jobDetail', job: screen.job });
      case 'think':
        return () => setScreen({ name: 'stop', job: screen.job });
      case 'act':
        return () => setScreen({ name: 'think', job: screen.job });
      case 'review':
        return () => setScreen({ name: 'act', job: screen.job });
      case 'sign':
        return () => setScreen({ name: 'review', job: screen.job });
      case 'done':
        return () => setScreen({ name: 'jobs' });
      case 'jobs':
        return undefined;
    }
  })();

  if (authError) {
    return <FluentProvider theme={harleysTheme}><SplashScreen error={authError} /></FluentProvider>;
  }
  if (!user) {
    return <FluentProvider theme={harleysTheme}><SplashScreen message="Signing you in…" /></FluentProvider>;
  }

  // Wait for both jobs and config before showing chrome — config drives all
  // hazard/PPE/regcheck lists, so showing the form without it would be wrong.
  const initialLoading =
    (jobsLoading && jobs.length === 0 && !jobsError) ||
    (!config && !configError);

  if (initialLoading) {
    return <FluentProvider theme={harleysTheme}><SplashScreen message="Loading your jobs…" /></FluentProvider>;
  }

  const showBreadcrumbs = ['stop', 'think', 'act', 'review', 'sign', 'done'].includes(screen.name);

  return (
    <FluentProvider theme={harleysTheme}>
      <div className={`${styles.app} take5-app`}>
        {!isInShell() && <HcpsHeader user={user} />}
        {showBreadcrumbs && <Breadcrumbs items={breadcrumbs} onBack={handleBreadcrumbBack} />}
        {screen.name === 'jobs' && (
          <JobsListScreen jobs={jobs} loading={jobsLoading} error={jobsError}
            onSelectJob={onSelectJob} onRefresh={loadJobs} />
        )}

        {screen.name === 'jobDetail' && (
          <JobDetailScreen
            job={screen.job}
            siteContact={siteContact}
            contactLoading={contactLoading}
            onBack={() => setScreen({ name: 'jobs' })}
            onStartT5={() => onStartT5(screen.job)}
          />
        )}

        {workflowActive && visitedSteps.has('stop') && 'job' in screen && config && (
          <div className="workflow-panel" hidden={screen.name !== 'stop'}>
          <StopScreen
            job={screen.job}
            config={config}
            initial={submission.stop || {}}
            profile={profile}
            supervisorCandidates={supervisorCandidates}
            siteContact={siteContact}
            onNext={stop => {
              setSubmission(s => ({ ...s, stop }));
              setScreen({ name: 'think', job: screen.job });
            }}
            onShowInfo={setInfoKey}
          />
          </div>
        )}

        {workflowActive && visitedSteps.has('think') && 'job' in screen && config && (
          <div className="workflow-panel" hidden={screen.name !== 'think'}>
          <ThinkScreen
            config={config}
            initial={submission.think || {}}
            onNext={think => {
              setSubmission(s => ({ ...s, think }));
              setScreen({ name: 'act', job: screen.job });
            }}
            onShowInfo={setInfoKey}
          />
          </div>
        )}

        {workflowActive && visitedSteps.has('act') && 'job' in screen && config && (
          <div className="workflow-panel" hidden={screen.name !== 'act'}>
          <ActScreen
            config={config}
            initial={submission.act || {}}
            onNext={act => {
              setSubmission(s => ({ ...s, act }));
              setScreen({ name: 'review', job: screen.job });
            }}
            onShowInfo={setInfoKey}
          />
          </div>
        )}

        {workflowActive && visitedSteps.has('review') && 'job' in screen && (
          <div className="workflow-panel" hidden={screen.name !== 'review'}>
          <ReviewScreen
            initial={submission.review || {}}
            siteContact={siteContact}
            onNext={review => {
              setSubmission(s => ({ ...s, review }));
              setScreen({ name: 'sign', job: screen.job });
            }}
            onShowInfo={setInfoKey}
          />
          </div>
        )}

        {workflowActive && visitedSteps.has('sign') && 'job' in screen && (
          <div className="workflow-panel" hidden={screen.name !== 'sign'}>
          <SignOffScreen job={screen.job} user={user} profile={profile}
            onSubmit={handleSubmit} submitting={Boolean(submitting)} />
          </div>
        )}

        {screen.name === 'done' && (
          <DoneScreen result={screen.result} jobNumber={screen.job.jobNumber}
            onBackToJobs={() => {
              setScreen({ name: 'jobs' });
              loadJobs();
            }} />
        )}

        {screen.name === 'admin' && config && (
          <AdminScreen
            isAdmin={isAdmin}
            config={config}
            onConfigChanged={loadConfig}
          />
        )}

        {submitError && <MessageBar intent="error" role="alert"><MessageBarBody>{submitError} Your answers and signature are retained. Correct any issue and try submitting again.</MessageBarBody></MessageBar>}
        <Dialog open={Boolean(pendingScreen)} onOpenChange={(_, data) => { if (!data.open) setPendingScreen(null); }}>
          <DialogSurface><DialogBody>
            <DialogTitle>Discard this Take 5?</DialogTitle>
            <DialogContent>Your answers and signature have not been submitted. Keep working to finish the safety check.</DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingScreen(null)}>Keep working</Button>
              <Button appearance="primary" onClick={() => { if (pendingScreen) updateScreen(pendingScreen); setPendingScreen(null); setSubmission({}); setSubmitError(null); }}>Discard Take 5</Button>
            </DialogActions>
          </DialogBody></DialogSurface>
        </Dialog>
        <InfoModal infoKey={infoKey} onClose={() => setInfoKey(null)} />

        {submitting && (
          <div className={`${styles.spinnerOverlay} take5-overlay-screen`}>
            <SafetyLoadingPanel message={submitting} />
          </div>
        )}
      </div>
    </FluentProvider>
  );
}
